import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../Components/Header/Header';
import Sidebar from '../../Components/Sidebar/Sidebar';
import { getQuoteRequests, acceptQuote, declineQuote, sendQuote } from '../../api/quotes';
import AdventureDateTimePicker from '../../Components/Shared/AdventureDateTimePicker';
import '../AdventureShared.css';
import './QuotesPage.css';
import ConfirmationModal from '../../Components/Shared/ConfirmationModal';

export default function QuotesPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize view mode based on pathname
  const initialMode = location.pathname.includes('/inbox') ? 'provider' : 'customer';
  const [viewMode, setViewMode] = useState(initialMode);

  const [quotes, setQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  // Modals state
  const [sendQuoteModalOpen, setSendQuoteModalOpen] = useState(false);
  const [declineModalOpen, setDeclineModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);

  // Send quote form states (Provider)
  const [quotedPrice, setQuotedPrice] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [scope, setScope] = useState('');
  const [proposedStartDate, setProposedStartDate] = useState('');
  const [proposedEndDate, setProposedEndDate] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [providerNotes, setProviderNotes] = useState('');

  // Decline form states
  const [declineReason, setDeclineReason] = useState('price_too_high');
  const [declineMessage, setDeclineMessage] = useState('');

  const currentUserId = localStorage.getItem("userId") || "";

  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const [isSendingQuote, setIsSendingQuote] = useState(false);
  const [quoteToAccept, setQuoteToAccept] = useState(null);

  // Sync viewMode if location changes
  useEffect(() => {
    const pathMode = location.pathname.includes('/inbox') ? 'provider' : 'customer';
    if (pathMode !== viewMode) {
      setViewMode(pathMode);
    }
  }, [location.pathname]);

  const handleToggleViewMode = (mode) => {
    setViewMode(mode);
    if (mode === 'provider') {
      navigate('/facilitate/quotes/inbox');
    } else {
      navigate('/facilitate/quotes');
    }
  };

  const fetchQuotes = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await getQuoteRequests({ role: viewMode });
      setQuotes(data);
    } catch (err) {
      console.error(`Failed to load quotes for role ${viewMode}:`, err);
      setError("Failed to load quote requests.");
    } finally {
      setIsLoading(false);
    }
  }, [viewMode]);

  useEffect(() => {
    fetchQuotes();
    setActiveFilter('all'); // Reset filter tab on mode switch
  }, [fetchQuotes]);

  // Adjust default decline reason when view mode changes
  useEffect(() => {
    setDeclineReason(viewMode === 'customer' ? 'price_too_high' : 'unavailable');
  }, [viewMode]);

  const formatPriceVal = (amount, curr) => {
    if (!amount) return 'N/A';
    let cleanCurrency = (curr || 'USD').trim().toUpperCase();
    if (cleanCurrency === 'RS') cleanCurrency = 'INR';
    if (!/^[A-Z]{3}$/.test(cleanCurrency)) cleanCurrency = 'USD';

    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: cleanCurrency }).format(amount);
    } catch (e) {
      return `${cleanCurrency} ${(amount || 0).toFixed(2)}`;
    }
  };

  const getFilteredQuotes = () => {
    const roleQuotes = quotes.filter(q => {
      if (viewMode === 'customer') {
        return q.customerId === currentUserId || q.customer?.userId === currentUserId;
      } else {
        return q.providerId === currentUserId || q.provider?.userId === currentUserId;
      }
    });

    if (activeFilter === 'all') return roleQuotes;

    if (viewMode === 'customer') {
      if (activeFilter === 'pending') {
        return roleQuotes.filter(q => q.status === 'requested' || q.status === 'pending');
      }
      return roleQuotes.filter(q => q.status === activeFilter);
    } else {
      if (activeFilter === 'new') {
        return roleQuotes.filter(q => q.status === 'requested' || q.status === 'pending');
      }
      return roleQuotes.filter(q => q.status === activeFilter);
    }
  };

  // Actions — Client
  const handleAcceptQuoteAction = (quote) => {
    setQuoteToAccept(quote);
  };

  const handleConfirmAcceptQuote = async () => {
    if (!quoteToAccept) return;
    setIsAccepting(true);
    try {
      const res = await acceptQuote(quoteToAccept.id);
      alert("Quote accepted successfully! An order reference has been generated.");
      setQuoteToAccept(null);
      if (res?.bookingId) {
        navigate(`/facilitate/orders/${res.bookingId}`);
      } else {
        fetchQuotes();
      }
    } catch (err) {
      console.error(err);
      alert("Failed to accept quote.");
    } finally {
      setIsAccepting(false);
    }
  };

  // Actions — Provider
  const handleOpenSendQuoteModal = (quoteReq) => {
    setSelectedQuote(quoteReq);
    setQuotedPrice(quoteReq.quotedPrice || quoteReq.budgetHint || '');
    setCurrency(quoteReq.currency || 'USD');
    setScope(quoteReq.scope || quoteReq.description || '');
    setProposedStartDate(quoteReq.proposedStartDate || quoteReq.preferredStartDate || '');
    setProposedEndDate(quoteReq.proposedEndDate || quoteReq.preferredEndDate || '');
    if (quoteReq.validUntil) {
      setValidUntil(quoteReq.validUntil);
    } else {
      const d = new Date();
      d.setDate(d.getDate() + 7);
      setValidUntil(d.toISOString().split('T')[0]);
    }
    setProviderNotes(quoteReq.notes || '');
    setSendQuoteModalOpen(true);
  };

  const handleSendQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!quotedPrice || !scope.trim() || !validUntil) {
      return alert("Please enter the quoted price, scope of work, and quote validity date.");
    }
    setIsSendingQuote(true);
    try {
      await sendQuote(selectedQuote.id, {
        quotedPrice: parseFloat(quotedPrice),
        currency,
        scope,
        proposedStartDate,
        proposedEndDate,
        validUntil,
        notes: providerNotes
      });
      setSendQuoteModalOpen(false);
      setSelectedQuote(null);
      fetchQuotes();
    } catch (err) {
      console.error(err);
      alert("Failed to send quote.");
    } finally {
      setIsSendingQuote(false);
    }
  };

  // Actions — Decline (Shared)
  const handleDeclineSubmit = async (e) => {
    e.preventDefault();
    setIsDeclining(true);
    try {
      await declineQuote(selectedQuote.id, declineReason, declineMessage);
      setDeclineModalOpen(false);
      setSelectedQuote(null);
      setDeclineMessage('');
      fetchQuotes();
    } catch (err) {
      console.error(err);
      alert("Failed to decline quote request.");
    } finally {
      setIsDeclining(false);
    }
  };

  const FILTERS = viewMode === 'customer' 
    ? ['all', 'pending', 'quoted', 'accepted', 'declined']
    : ['all', 'new', 'quoted', 'accepted', 'declined', 'expired'];

  return (
    <div className="QuotesPage">
      <div className="adventurePageContainer">
        <div className="headerContain"><Header /></div>

        <div className="adventurePageContainer2">
          <div className="sidebarContain"><Sidebar /></div>

          <main className="adv-page-main">
            {/* View Switcher and Page Header */}
            <div className="adv-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
              <div>
                <h1 className="adv-page-title">
                  {viewMode === 'customer' ? (
                    <>My Quote <span className="adv-page-title-accent">Requests</span></>
                  ) : (
                    <>Quote Requests <span className="adv-page-title-accent">Inbox</span></>
                  )}
                </h1>
                <p className="adv-page-sub">
                  {viewMode === 'customer' 
                    ? "Track your tailored project inquiries and manage offers sent by providers."
                    : "Manage custom client inquiries, send formal quotes, or discuss project scopes."
                  }
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '12px' }}>
                {/* View Mode Switcher Toggle */}
                <div className="adv-quotes-switcher" style={{ margin: 0 }}>
                  <button
                    className={`adv-quotes-switch-btn ${viewMode === 'customer' ? 'adv-quotes-switch-btn--active' : ''}`}
                    onClick={() => handleToggleViewMode('customer')}
                  >
                    🧑 My Purchases
                  </button>
                  <button
                    className={`adv-quotes-switch-btn ${viewMode === 'provider' ? 'adv-quotes-switch-btn--active' : ''}`}
                    onClick={() => handleToggleViewMode('provider')}
                  >
                    💼 My Sales / Requests
                  </button>
                </div>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="adv-filter-tabs">
              {FILTERS.map(tab => (
                <button
                  key={tab}
                  className={`adv-filter-tab ${activeFilter === tab ? 'adv-filter-tab--active' : ''}`}
                  onClick={() => setActiveFilter(tab)}
                >
                  {tab === 'new' && viewMode === 'provider' ? 'New Requests' : tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Quote Requests List */}
            <div className="adv-list">
              {isLoading ? (
                <div className="adv-state-text">Loading quote requests...</div>
              ) : error ? (
                <div className="adv-state-error">{error}</div>
              ) : getFilteredQuotes().length === 0 ? (
                <div className="adv-empty">
                  <p>No quote requests found for the selected filter.</p>
                </div>
              ) : (
                getFilteredQuotes().map((quote) => (
                  <div key={quote.id} className="adv-request-card">
                    {/* Top Row: User Avatar, Name, Request Date, and Status Badge */}
                    <div className="adv-request-card__top">
                      <div className="adv-request-card__customer">
                        <img
                          src={
                            viewMode === 'customer' 
                              ? quote.providerAvatarUrl || "/profilePic.jpg"
                              : quote.customerAvatarUrl || "/profilePic.jpg"
                          }
                          alt={viewMode === 'customer' ? quote.providerName : quote.customerName}
                          className="adv-request-card__avatar"
                        />
                        <div>
                          <h4 className="adv-request-card__name">
                            {viewMode === 'customer' ? quote.providerName : quote.customerName}
                          </h4>
                          <span className="adv-request-card__time">
                            {viewMode === 'customer' ? 'Requested' : 'Submitted'} on {new Date(quote.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <span className={`adv-status adv-status--${quote.status}`}>
                        {quote.status === 'requested' 
                          ? (viewMode === 'customer' ? 'Pending Provider Review' : 'New Request') 
                          : quote.status
                        }
                      </span>
                    </div>

                    {/* Detail Grid */}
                    <div className="adv-request-card__detail-grid">
                      <div>
                        <p className="adv-detail-label">
                          {viewMode === 'customer' ? 'My Request Description:' : 'Client Request Description:'}
                        </p>
                        <p className="adv-detail-notes" style={{ color: '#e6edf3', fontWeight: 500 }}>
                          {quote.description}
                        </p>

                        {/* File Attachments (Provider view mostly, or shared) */}
                        {quote.attachmentUrls?.length > 0 && (
                          <div style={{ marginTop: '12px' }}>
                            <p className="adv-detail-label">Attachments ({quote.attachmentUrls.length}):</p>
                            <div className="adv-evidence-grid">
                              {quote.attachmentUrls.map((url, i) => (
                                <a key={i} href={url} target="_blank" rel="noreferrer">
                                  <img src={url} alt="Attachment" className="adv-evidence-img" />
                                </a>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Render Scope Offer (if provider already quoted) */}
                        {quote.status === 'quoted' && (
                          <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(88, 166, 255, 0.08)', borderRadius: '8px', borderLeft: '3px solid #58a6ff' }}>
                            <p className="adv-detail-label" style={{ color: '#58a6ff' }}>Provider Scope Offer:</p>
                            <p className="adv-detail-notes" style={{ color: '#c9d1d9' }}>{quote.scope}</p>
                            {quote.validUntil && (
                              <p className="adv-detail-sub" style={{ marginTop: '6px', color: '#f5c842' }}>
                                ⏳ Quote valid until {quote.validUntil}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Right Details & Action Buttons */}
                      <div className="adv-request-card__right">
                        <div>
                          {quote.status === 'quoted' ? (
                            <div>
                              <p className="adv-detail-label">Quoted Price:</p>
                              <div className="adv-request-card__price" style={{ color: '#28a745' }}>
                                {formatPriceVal(quote.quotedPrice, quote.currency)}
                              </div>
                            </div>
                          ) : (
                            <div>
                              <p className="adv-detail-label">
                                {viewMode === 'customer' ? 'My Budget Hint:' : 'Client Budget Hint:'}
                              </p>
                              <strong className="adv-detail-value">
                                {quote.budgetHint > 0 ? formatPriceVal(quote.budgetHint, quote.currency) : "Flexible"}
                              </strong>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="adv-request-card__btns">
                          <button
                            className="adv-btn-outline"
                            onClick={() => navigate(`/facilitate/quotes/${quote.id}`)}
                          >
                            View Full Details
                          </button>

                          {/* Client Actions */}
                          {viewMode === 'customer' && quote.status === 'quoted' && (
                            <>
                              <button
                                className="adv-btn-danger-outline"
                                onClick={() => { setSelectedQuote(quote); setDeclineModalOpen(true); }}
                              >
                                Decline Offer
                              </button>
                              <button
                                className="adv-btn-success"
                                onClick={() => handleAcceptQuoteAction(quote)}
                                disabled={isAccepting}
                              >
                                {isAccepting ? "Accepting..." : "Accept Quote"}
                              </button>
                            </>
                          )}

                          {/* Provider Actions */}
                          {viewMode === 'provider' && (quote.status === 'requested' || quote.status === 'pending') && (
                            <>
                              <button
                                className="adv-btn-danger-outline"
                                onClick={() => { setSelectedQuote(quote); setDeclineModalOpen(true); }}
                              >
                                Decline Request
                              </button>
                              <button
                                className="adv-btn-success"
                                onClick={() => handleOpenSendQuoteModal(quote)}
                              >
                                Send Quote Offer
                              </button>
                            </>
                          )}

                          {/* Universal route back to Order if accepted */}
                          {quote.status === 'accepted' && quote.bookingId && (
                            <button
                              className="adv-btn-primary"
                              onClick={() => navigate(`/facilitate/orders/${quote.bookingId}`)}
                            >
                              View Order
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Decline Quote Modal (Shared) */}
      {declineModalOpen && selectedQuote && (
        <div className="adv-modal-overlay">
          <div className="adv-modal">
            <h2 className="adv-modal__title">
              {viewMode === 'customer' ? "Decline this quote?" : "Decline quote request?"}
            </h2>
            <p className="adv-modal__desc">
              {viewMode === 'customer' ? (
                <>Decline quote offer from <strong>{selectedQuote.providerName}</strong>.</>
              ) : (
                <>Decline custom quote inquiry from <strong>{selectedQuote.customerName}</strong>.</>
              )}
            </p>
            <form onSubmit={handleDeclineSubmit}>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Reason *</label>
                <select value={declineReason} onChange={(e) => setDeclineReason(e.target.value)} className="adv-modal__select">
                  {viewMode === 'customer' ? (
                    <>
                      <option value="price_too_high">Price too high</option>
                      <option value="dates_dont_work">Proposed dates don't work</option>
                      <option value="found_another_provider">Found another provider</option>
                      <option value="changed_mind">Changed my mind</option>
                      <option value="other">Other</option>
                    </>
                  ) : (
                    <>
                      <option value="unavailable">Unavailable for this job</option>
                      <option value="budget_too_low">Offered budget is too low</option>
                      <option value="schedule_conflict">Schedule conflict</option>
                      <option value="other">Other</option>
                    </>
                  )}
                </select>
              </div>
              <div className="adv-modal__field">
                <label className="adv-modal__label">Message to {viewMode === 'customer' ? 'provider' : 'customer'} (optional)</label>
                <textarea
                  rows="3"
                  placeholder="Leave a polite note explaining your decision..."
                  value={declineMessage}
                  onChange={(e) => setDeclineMessage(e.target.value)}
                  className="adv-modal__textarea"
                />
              </div>
              <div className="adv-modal-actions">
                <button type="button" className="adv-btn-ghost" onClick={() => { setDeclineModalOpen(false); setSelectedQuote(null); }} disabled={isDeclining}>Cancel</button>
                <button 
                  type="submit" 
                  className="adv-btn-destructive"
                  disabled={isDeclining}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isDeclining ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      Declining...
                    </>
                  ) : (
                    "Confirm Decline"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Send Quote Offer Modal (Provider Only) */}
      {sendQuoteModalOpen && selectedQuote && (
        <div className="adv-modal-overlay">
          <div className="adv-modal" style={{ maxWidth: '650px' }}>
            <h2 className="adv-modal__title">Send Custom Quote</h2>
            <form onSubmit={handleSendQuoteSubmit}>
              <div className="adv-form-grid-2">
                <div className="adv-modal__field">
                  <label className="adv-modal__label">Quoted Price *</label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    placeholder="e.g. 450.00"
                    value={quotedPrice}
                    onChange={(e) => setQuotedPrice(e.target.value)}
                    className="adv-modal__input"
                  />
                </div>
                <div className="adv-modal__field">
                  <label className="adv-modal__label">Currency *</label>
                  <select value={currency} onChange={(e) => setCurrency(e.target.value)} className="adv-modal__select">
                    <option value="USD">USD</option>
                  </select>
                </div>
              </div>

              <div className="adv-modal__field">
                <label className="adv-modal__label">Scope of Work *</label>
                <textarea
                  required
                  rows="4"
                  placeholder="Detail exactly what is included in this quoted price..."
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                  className="adv-modal__textarea"
                />
              </div>

              <div className="adv-form-grid-2">
                <div className="adv-modal__field">
                  <AdventureDateTimePicker
                    type="date"
                    label="Proposed Start Date"
                    value={proposedStartDate}
                    onChange={setProposedStartDate}
                    minDate={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div className="adv-modal__field">
                  <AdventureDateTimePicker
                    type="date"
                    label="Proposed End Date"
                    value={proposedEndDate}
                    onChange={setProposedEndDate}
                    minDate={proposedStartDate || new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div className="adv-modal__field">
                <AdventureDateTimePicker
                  type="date"
                  label="Quote Valid Until (Expiry Date) *"
                  value={validUntil}
                  onChange={setValidUntil}
                  minDate={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="adv-modal__field">
                <label className="adv-modal__label">Notes to Customer (Optional)</label>
                <textarea
                  rows="2"
                  placeholder="Any extra notes or conditions..."
                  value={providerNotes}
                  onChange={(e) => setProviderNotes(e.target.value)}
                  className="adv-modal__textarea"
                />
              </div>

              <div className="adv-modal-actions">
                <button type="button" className="adv-btn-ghost" onClick={() => { setSendQuoteModalOpen(false); setSelectedQuote(null); }} disabled={isSendingQuote}>Cancel</button>
                <button 
                  type="submit" 
                  className="adv-btn-primary"
                  disabled={isSendingQuote}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}
                >
                  {isSendingQuote ? (
                    <>
                      <span className="adv-btn-spinner"></span>
                      Sending...
                    </>
                  ) : (
                    "Send Quote"
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={quoteToAccept !== null}
        onClose={() => setQuoteToAccept(null)}
        onConfirm={handleConfirmAcceptQuote}
        title="Accept Custom Quote?"
        message={quoteToAccept ? `Are you ready to accept the quote of ${formatPriceVal(quoteToAccept.quotedPrice, quoteToAccept.currency)}? This will create a confirmed booking.` : ""}
        confirmText="Yes, Accept Quote"
        cancelText="Cancel"
        isDanger={false}
        isLoading={isAccepting}
      />
    </div>
  );
}
